/**
 * UserService – manages user data and customer number selection.
 *
 * - For role `customer`: customer number is automatically taken from the
 *   token payload (`customerId`).
 * - For role `employee` / `admin`: customer number is manually entered
 *   and stored in sessionStorage for the session duration.
 *
 * Validates: Requirements 8.1–8.5
 */

import { authService } from '../auth/auth.service';
import { eventBus } from '../../infrastructure/event-bus';
import { padCustomerNumber } from '../../shared/utils/pad-customer-number';
import type { LoginUser } from '../../shared/models/login-user.model';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** SessionStorage key for the manually entered customer number. */
export const CUSTOMER_NUMBER_KEY = 'snaphub_customer_number';

// ---------------------------------------------------------------------------
// UserService class
// ---------------------------------------------------------------------------

export class UserService {
  /**
   * Get the currently logged-in user.
   */
  getUser(): LoginUser | null {
    return authService.getUser();
  }

  /**
   * Get the active customer number.
   *
   * - `customer` role → automatic from token payload (`customerId`)
   * - `employee` / `admin` role → manually entered value (session-scoped)
   *
   * Returns `null` if no customer number is available.
   */
  getCustomerNumber(): string | null {
    const user = authService.getUser();
    if (!user) return null;

    if (user.role === 'customer') {
      return user.customerId ?? null;
    }

    // employee / admin → use manually entered number from sessionStorage
    return sessionStorage.getItem(CUSTOMER_NUMBER_KEY);
  }

  /**
   * Set the customer number manually (for employees / admins).
   * Stored in sessionStorage for the session duration — persists across
   * page refreshes within the same tab.
   * Automatically pads with leading zeros to 10 digits.
   */
  setCustomerNumber(customerNumber: string): void {
    const padded = padCustomerNumber(customerNumber);
    sessionStorage.setItem(CUSTOMER_NUMBER_KEY, padded);
    eventBus.emit('user-changed');
  }

  /**
   * Returns `true` when the current user is an employee or admin
   * (i.e. needs to manually enter a customer number).
   */
  isEmployee(): boolean {
    const user = authService.getUser();
    if (!user) return false;
    return user.role === 'employee' || user.role === 'admin';
  }

  /**
   * Subscribe to user-related changes.
   * Returns an unsubscribe function.
   */
  onUserChanged(callback: () => void): () => void {
    return eventBus.on('user-changed', () => callback());
  }
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------

/** Global UserService instance shared across the application. */
export const userService = new UserService();

/**
 * Reset the manual customer number. Useful for testing.
 * @internal
 */
export function _resetManualCustomerNumber(): void {
  sessionStorage.removeItem(CUSTOMER_NUMBER_KEY);
}
